import { useEffect, useState } from "react"
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { Box, Button, Card, Divider, Grid, IconButton, Switch, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { IHeaderGrid } from "../../interfaces/grid/IHeaderGrid";
import GridContent from "../../components/grid";
import GridHeader from "../../components/grid/header";
import { tryGetAllWalletList, tryRemoveWalletById, tryToogleWalletIsActive, tryToogleWalletIsBalanced } from "../../services/wallet/wallet-service";
import IWalletDto from "../../interfaces/wallet/IWalletDto";
import { useNotification } from "../../components/notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";

const headerColumnList: IHeaderGrid[] = [
    {
        align: 'left',
        name: 'Activo',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Carteira de Balanceamento',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Nome da Carteira',
        padding: 'normal'
    },
    {
        align: 'right',
        name: 'Editar/Remover',
        padding: 'normal'
    },
];

export default function Wallet() {

    const navigate = useNavigate();
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [walletList, setWalletList] = useState<IWalletDto[]>();

    useEffect(() => {

        async function getAllWalletList() {
            setLoading(true);
            try {
                var response = (await tryGetAllWalletList()).data;

                if (response) {
                    setWalletList(response);
                }
            }
            catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
            }
            finally {
                setLoading(false);
            }
        }
        getAllWalletList();
    }, []);


    const handleRemoveWallet = async (walletId: string) => {
        setLoading(true);

        try {
            var response = await tryRemoveWalletById(walletId);

            if (response?.status === 202) {
                displayNotification( {type:'success', message: 'Carteira removida com sucesso.'} );

                var newWalletList = walletList?.filter(x => x.id != walletId);

                if (newWalletList) {
                    setWalletList(newWalletList);
                }
            }
        }
        catch (error) {
            displayNotification({type:'error', message: extractErrorMessage(error)});
        }
        finally {
            setLoading(false);
        }
    }

    async function handleIsActiveOnChange(wallet: IWalletDto) {
        setLoading(true);

        try {
            var response = await tryToogleWalletIsActive(wallet.id);
            if (response?.status === 200) {
                const newStateWallet = walletList;
                const oldWallet = newStateWallet?.filter((obj) => obj.id === wallet.id)[0];
                newStateWallet!.filter((obj) => obj.id === wallet.id)[0].isActive = !oldWallet?.isActive
                setWalletList(newStateWallet);
                displayNotification({ type: 'success', message: 'Carteira actualizada com sucesso' });
            }
        }
        catch (error) {
            displayNotification({ type:'error', message: extractErrorMessage(error) });
        }
        finally {
            setLoading(false);
        }
    }

    async function handleIsBalancedWalletOnChange(wallet: IWalletDto) {
        setLoading(true);

        try {
            var response = await tryToogleWalletIsBalanced(wallet.id);

            if (response?.status === 200) {
                const newStateWallet = walletList;
                const oldWallet = newStateWallet?.filter((obj) => obj.id === wallet.id)[0];
                newStateWallet!.filter((obj) => obj.id === wallet.id)[0].isBalancedWallet = !oldWallet?.isBalancedWallet
                setWalletList(newStateWallet);
                displayNotification({ type: 'success', message: 'Carteira actualizada com sucesso' });
            }
        }
        catch (error) {
            displayNotification({ type:'error', message: extractErrorMessage(error) });
        }
        finally {
            setLoading(false);
        }
    }

    return (
        <>
            {showLoading === true ? <Loader /> : null}
            <Box mt={3} mb={1}>
                <Grid container justifyContent="space-between" alignItems="flex-start">
                    <Grid>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                            Gestão de Carteira
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Consulta e gestão das tuas carteiras
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button variant="contained" color="primary" onClick={() => navigate(getBaseURLWithAuth() + 'wallet/create')}>
                            Nova Carteira
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
            </Box>
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <Box>
                    <GridContent>
                        <GridHeader headerProps={headerColumnList} />
                        <TableBody>
                            {walletList && walletList.map((wallet, index) => (
                                <TableRow
                                    key={wallet.id}
                                    sx={{
                                        backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell align="left">
                                        <Grid>
                                            <Switch checked={wallet.isActive} onChange={() => handleIsActiveOnChange(wallet)}/>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Switch checked={wallet.isBalancedWallet} onChange={() => handleIsBalancedWalletOnChange(wallet)} />
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            {wallet.walletName}
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Grid container justifyContent="flex-end">
                                            <Grid>
                                                <IconButton color="info" onClick={() => navigate(getBaseURLWithAuth() + `wallet/edit/${wallet.id}`)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Grid>
                                            <Grid>
                                                <IconButton color="error" onClick={() => handleRemoveWallet(wallet.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </GridContent>
                </Box>
            </Card>
        </>
    )
}